"use client";
import React, { useEffect, useState, useMemo } from "react";

import Header from "../../layouts/Header";
import DigitalWalletGradient from "../../components/DigitalWalletGradient";
import { InfoIcon } from "lucide-react";
import { useLocation } from "react-router-dom";
import { TransactionHistory } from "../../components/transaction-history";
import SendReceiveForex from "../../components/SendReceiveForex";
import BuySellPageForex from "../../components/BuySellPageForex";
import {
  useCryptoAssets,
  useFetchAllTradablePairs,
  useNetworksByAssets,
} from "../../hooks/query/queries";
import SideNavForex from "../../components/SideNavForex";
import { usePairStore } from "../../store/useStore";
import { useWalletBalance } from "../../hooks/query/queries";
import HowToTrade from "../../components/HowToTrade";
import Toaster from "../../components/ui/toaster";
import ForexAssets from "../../components/ForexAssets";
import { useAuth } from "../../context/AuthContext";

export default function ForexDashboard() {
  const location = useLocation();
  const {
    tabActive,
    selectedCrypto,
    type,
    selectedTab,
    parentTab,
    childTab,
    parentNavTab,
  } = location.state || {};

  const { setPairObject } = usePairStore();
  const [, setLoadingAsset] = useState(false);
  const { data: tradingAssets } = useCryptoAssets();
  const { data: pairObject } = useFetchAllTradablePairs();

  useEffect(() => {
    if (pairObject) {
      setPairObject(pairObject);
    }
  }, [pairObject, setPairObject]);

  const { data: tradingNets } = useNetworksByAssets(
    tradingAssets,
    setLoadingAsset
  );

  const [activeTab, setActiveTab] = useState("overview");
  const [transactionTab, setTransactionTab] = useState("");
  const [asset, setAsset] = useState(null);
  const [open, setOpen] = useState(false);
  const { user: userProfile } = useAuth();

  const userId = userProfile?._id;
  const { data: walletBalanceData } = useWalletBalance();

  const wallet = useMemo(() => {
    if (!walletBalanceData) return null;
    const portfolioData = walletBalanceData;
    return {
      ...portfolioData,
      assetBalance: portfolioData?.assets || [],
      assetBalances: {
        crypto: (portfolioData?.assets || []).filter(
          (asset) => asset.currencyType === "Crypto" || asset.type === "Crypto"
        ),
        fiat: (portfolioData?.assets || []).filter(
          (asset) => asset.currencyType === "Fiat" || asset.type === "Fiat"
        ),
      },
    };
  }, [walletBalanceData]);

  const assetBalance = useMemo(() => wallet?.assetBalance || [], [wallet]);

  useEffect(() => {
    if (parentTab) {
      setActiveTab(parentTab);
    }
    if (type) {
      setTransactionTab(tabActive);
      setActiveTab(type);
      setAsset(selectedCrypto);
    }
    if (parentNavTab) {
      setActiveTab(parentNavTab);
    }
  }, [parentTab, type, parentNavTab, tabActive, selectedCrypto]);

  const handleTransact = (asset, type) => {
    setAsset(asset);
    if (type === "a") {
      setActiveTab("buy-sell");
      setTransactionTab("Buy");
    }
    if (type === "b") {
      setActiveTab("buy-sell");
      setTransactionTab("Sell");
    }
    if (type === "c") {
      setActiveTab("send-receive");
      setTransactionTab("send");
    }
    if (type === "d") {
      setActiveTab("send-receive");
      setTransactionTab("receive");
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <Header />
      <DigitalWalletGradient
        title="Wallet Balance"
        wallet={wallet}
        assetBalance={assetBalance}
        onCurrencyChange={() => {}}
        userId={userId}
      />
      <div className="relative -mt-14">
        <div
          onClick={() => setOpen(true)}
          className="bg-gradient-to-r from-blue-600 to-purple-500 rounded-t-3xl pt-4 px-6 w-full h-20 mx-auto flex items-start justify-center -mt-2 cursor-pointer"
        >
          <InfoIcon size={20} className="mr-2 text-white" />
          <h1 className="text-white text-sm underline hover:text-md hover:text-red-50">
            How to use Forex
          </h1>
        </div>
        <HowToTrade open={open} setOpen={setOpen} setActiveTab={setActiveTab} />
        <div className="bg-gray-100 rounded-t-3xl -mt-8 rounded-b-3xl pt-8 px-6 w-full h-20 mx-auto" />
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="flex min-h-[700px]">
            {/* Sidebar: keep at 1/4 width */}
            <aside className="w-1/4 mr-6">
              <SideNavForex
                activeTab={activeTab}
                onTabChange={(tabId) => setActiveTab(tabId)}
              />
            </aside>

            {/* Main content: 3/4 width */}
            <Toaster />
            <main className="w-3/4 space-y-8 mb-10">
              {activeTab === "overview" && (
                <>
                  <ForexAssets onTransact={handleTransact} userId={userId} />
                  {/* <Orders /> */}
                  <TransactionHistory userId={userId} />
                </>
              )}

              {activeTab === "buy-sell" && (
                <BuySellPageForex
                  assetToSelect={asset}
                  pairObject={pairObject}
                  tab={transactionTab}
                  quickActionTab={selectedTab}
                  childTab={childTab}
                  tradingAssets={tradingAssets}
                  selectedCrypto
                />
              )}

              {activeTab === "send-receive" && (
                <SendReceiveForex
                  assetToSelect={asset}
                  tab={transactionTab}
                  linkTab={selectedTab}
                  tradingNets={tradingNets}
                  tradingAssets={tradingAssets}
                  childTab={childTab}
                />
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
